import { useMutation, type PureQueryOptions } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { useZodForm } from '~/utils/forms';
import { faciesSchema, initialFacies } from '~/utils/modules/supporting-object';

const CREATE_FACIES = graphql(`
  mutation CreateFacies($input: FaciesSchemaCreateInput!) {
    faciesSchemaCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

type Props = SOParent & {
  refetchQueries?: PureQueryOptions[];
};

export function FaciesCreateForm({
  outcropId,
  studyId,
  refetchQueries,
}: Props) {
  const [createFacies, { loading, error }] = useMutation(CREATE_FACIES, {
    refetchQueries,
  });

  const form = useZodForm({
    schema: faciesSchema,
    values: initialFacies(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createFacies({
        variables: {
          input: {
            ...values,
            outcropId,
            studyId,
          },
        },
      });
      toast.success('Facies created successfully.');
      form.reset();
    } catch (err) {
      console.log('Error creating facies', err);
      toast.error('There was a problem creating the facies schema.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="FACIES_SCHEMA" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
